import React, { useEffect, useRef, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap"
import Select from "react-select"
import DateRangePicker from "components/Common/DateRangePicker"
import { bsh_getAdminUserDetails } from "helpers/sf_browser_storage_helper"
import { REPORTING_TIMEZONE } from "helpers/sf_constants"
import {
  dateRangeToReportingDates,
  resolveClientToken,
} from "./reportingConsistencyHelper"

import { EXPORT_TYPES, runExportValidation } from "./exportValidationRunner"

const ALL_EXPORT_OPTION = { value: "all", label: "All export cases" }
const EXPORT_OPTIONS = [ALL_EXPORT_OPTION, ...EXPORT_TYPES]

const ExportValidationTab = ({
  accountId,
  onAccountIdChange,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
}) => {
  const [selectedExportTypes, setSelectedExportTypes] = useState(EXPORT_TYPES)
  const [status, setStatus] = useState("idle")
  const [progress, setProgress] = useState("")
  const [results, setResults] = useState([])
  const [error, setError] = useState("")
  const activeRun = useRef(0)
  useEffect(
    () => () => {
      activeRun.current += 1
    },
    []
  )

  useEffect(() => {
    activeRun.current += 1
    setResults([])
    setError("")
    setStatus("idle")
    setProgress("")
  }, [accountId, selectedDateRange])

  const allSelected = selectedExportTypes.length === EXPORT_TYPES.length
  const selectedOptions = allSelected
    ? [ALL_EXPORT_OPTION]
    : selectedExportTypes
  const selectExportTypes = options => {
    const selected = options || []
    if (selected.some(option => option.value === ALL_EXPORT_OPTION.value)) {
      setSelectedExportTypes(EXPORT_TYPES)
      return
    }
    setSelectedExportTypes(
      selected
        .map(option => EXPORT_TYPES.find(item => item.value === option.value))
        .filter(Boolean)
    )
  }

  const updateResult = (exportType, update) => {
    setResults(previous => {
      const existing = previous.findIndex(
        result => result.export_type === exportType
      )
      if (existing < 0)
        return [...previous, { export_type: exportType, ...update }]
      return previous.map((result, index) =>
        index === existing ? { ...result, ...update } : result
      )
    })
  }

  const run = async () => {
    const runId = ++activeRun.current
    const isCancelled = () => activeRun.current !== runId
    const updateCase = (label, update) => {
      if (!isCancelled()) updateResult(label, update)
    }
    const adminToken = bsh_getAdminUserDetails()?.token
    if (!adminToken) {
      setError("An authenticated admin session is required.")
      return
    }
    if (!accountId || !Number(accountId)) {
      setError("Account ID is required.")
      return
    }

    const dates = dateRangeToReportingDates(selectedDateRange)
    setStatus("running")
    setProgress(
      `Running ${selectedExportTypes.length} export validations in parallel. See each case status below.`
    )
    setError("")
    setResults(
      selectedExportTypes.map(selectedType => ({
        export_type: selectedType.label,
        status: "queued",
      }))
    )

    try {
      const clientToken = await resolveClientToken(accountId, adminToken)
      if (isCancelled()) return
      await Promise.all(
        selectedExportTypes.map(async selectedType => {
          const onStatus = update => updateCase(selectedType.label, update)
          try {
            const result = await runExportValidation({
              selectedType,
              dates,
              accountId,
              clientToken,
              adminToken,
              onStatus,
              isCancelled,
            })
            if (result) onStatus(result)
          } catch (caseError) {
            onStatus({
              status: caseError.code || "ERROR",
              differences: { error: caseError.message || String(caseError) },
            })
          }
        })
      )
      if (isCancelled()) return
      setStatus("completed")
      setProgress(`Completed ${selectedExportTypes.length} export validations.`)
    } catch (runError) {
      if (isCancelled()) return
      setStatus("failed")
      setError(runError.message)
      setResults(previous => previous.map(result => ({
        ...result,
        status: "ERROR",
        differences: { error: runError.message },
      })))
    }
  }

  const stopWaiting = () => {
    activeRun.current += 1
    setStatus("stopped")
    setProgress(
      "Polling stopped. Submitted export jobs continue in the background."
    )
  }

  return (
    <Card className="shadow-sm border-0">
      <CardBody className="p-4">
        <h4 className="card-title mb-1">Export validation</h4>
        <p className="text-muted">
          Run the matching page API and backend export, then compare the
          complete filtered result.
        </p>
        <Row className="g-3 align-items-end">
          <Col md="6">
            <Label className="font-weight-bold">Export cases</Label>
            <Select
              isMulti
              closeMenuOnSelect={false}
              isSearchable={false}
              isDisabled={status === "running"}
              options={EXPORT_OPTIONS}
              value={selectedOptions}
              onChange={selectExportTypes}
              isOptionDisabled={option =>
                allSelected && option.value !== ALL_EXPORT_OPTION.value
              }
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              classNamePrefix="reporting-consistency-select"
            />
          </Col>
          <Col md="2">
            <Label className="font-weight-bold">Account ID</Label>
            <Input
              disabled={status === "running"}
              type="number"
              min="1"
              value={accountId}
              onChange={event => onAccountIdChange(event.target.value)}
            />
          </Col>
          <Col md="2">
            <Label className="font-weight-bold">Timeframe</Label>
            <DateRangePicker
              ianaTz={REPORTING_TIMEZONE}
              value={selectedDateRange}
              options={dateRangeOptions}
              onChange={onDateRangeChange}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </Col>
          <Col md="2">
            <Button
              color="primary"
              className="w-100"
              disabled={
                status === "running" ||
                !accountId ||
                !selectedExportTypes.length
              }
              onClick={run}
            >
              {status === "running" ? "Running..." : "Run validations"}
            </Button>
            {status === "running" && (
              <Button color="secondary" className="mt-2" onClick={stopWaiting}>
                Stop waiting
              </Button>
            )}
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md="12">
            <small className="form-text text-muted">
              The selected cases use the account and reporting date range above.
              Grouping and ad-hoc filters are intentionally fixed for export
              parity. Detail exports use the first record with detail data
              within the selected timeframe. NO_DATA does not prove non-empty
              export parity.
            </small>
          </Col>
        </Row>
        {progress && (
          <div className="alert alert-info mt-3 mb-0">{progress}</div>
        )}
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        {results.length > 0 && (
          <Table bordered responsive className="mt-3 mb-0">
            <thead>
              <tr>
                <th>Export case</th>
                <th>Status</th>
                <th>Page rows</th>
                <th>CSV rows</th>
                <th>Differences</th>
              </tr>
            </thead>
            <tbody>
              {results.map(result => (
                <tr key={result.export_type}>
                  <td>{result.export_type}</td>
                  <td>{result.status}</td>
                  <td>{result.page_rows ?? result.page_fields}</td>
                  <td>{result.csv_rows ?? result.csv_fields}</td>
                  <td>
                    <pre className="mb-0">
                      {JSON.stringify(result.differences || {}, null, 2)}
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  )
}

export default ExportValidationTab
